import { Injectable } from '@angular/core';
import { Query } from '@datorama/akita';
import { CloseReason, CustomRxEventType, RxCompleteData, RxStatusData, RxUIReadyData } from '../models/lab-broker-event.model';
import { LabBrokerConfig, LabBrokerState, LabBrokerViewState } from './lab-broker.model';
import { LabBrokerStore } from './lab-broker.store';

@Injectable({ providedIn: 'root' })
export class LabBrokerQuery extends Query<LabBrokerState> {
	readonly isActive$ = this.select(s => s.viewState !== LabBrokerViewState.Inactive);
	readonly viewState$ = this.select(s => s.viewState);
	readonly isLoading$ = this.select(s => s.viewState === LabBrokerViewState.Loading);
	readonly isError$ = this.select(s => s.viewState === LabBrokerViewState.Error);
	readonly isClosing$ = this.select(s => s.viewState === LabBrokerViewState.Closing);
	readonly iframeUrl$ = this.select(s => s.config?.iframeUrl ?? null);
	readonly integratorName$ = this.select(s => s.config?.integratorName ?? null);
	readonly errorMessage$ = this.select(s => s.errorMessage);
	readonly fullCustomRxPayload$ = this.select(s => s.fullCustomRxPayload);
	readonly isUnavailable$ = this.select(s => s.viewState === LabBrokerViewState.Unavailable);
	readonly pendingCloseReason$ = this.select(s => s.pendingCloseReason);
	readonly isClosePending$ = this.select(s => s.pendingCloseReason !== null);

	readonly isCompleted$ = this.select(s => s.viewState === LabBrokerViewState.Completed);

	readonly isFooterVisible$ = this.select(
		s =>
			s.viewState === LabBrokerViewState.Active ||
			s.viewState === LabBrokerViewState.Completed ||
			s.viewState === LabBrokerViewState.Closing
	);

	get viewState(): LabBrokerViewState {
		return this.getValue().viewState;
	}

	get isActive(): boolean {
		return this.getValue().viewState !== LabBrokerViewState.Inactive;
	}

	get isClosing(): boolean {
		return this.getValue().viewState === LabBrokerViewState.Closing;
	}

	/** Unlike `isClosing`, stays true when an RxComplete moves the view out of Closing mid-close. */
	get isClosePending(): boolean {
		return this.getValue().pendingCloseReason !== null;
	}

	get requiresCloseHandshake(): boolean {
		const { viewState, pendingCloseReason } = this.getValue();

		return viewState === LabBrokerViewState.Active && pendingCloseReason === null;
	}

	get isUnavailable(): boolean {
		return this.getValue().viewState === LabBrokerViewState.Unavailable;
	}

	get isDialogSuppressed(): boolean {
		return this.getValue().dentiraUnavailableDialogSuppressed;
	}

	get brokerConfig(): LabBrokerConfig | null {
		return this.getValue().config;
	}

	get integratorName(): string | null {
		return this.getValue().config?.integratorName ?? null;
	}

	get fullCustomRxPayload(): RxCompleteData['FullCustomRx'] | null {
		return this.getValue().fullCustomRxPayload;
	}

	get rxUIReadyData(): RxUIReadyData | null {
		return this.getValue().lastReceivedEvent[CustomRxEventType.RxUIReady]?.data ?? null;
	}

	get rxCompleteData(): RxCompleteData | null {
		return this.getValue().lastReceivedEvent[CustomRxEventType.RxComplete]?.data ?? null;
	}

	get lastRxStatusData(): RxStatusData | null {
		return this.getValue().lastReceivedEvent[CustomRxEventType.RxStatus]?.data ?? null;
	}

	get pendingCloseReason(): CloseReason | null {
		return this.getValue().pendingCloseReason;
	}

	constructor(protected store: LabBrokerStore) {
		super(store);
	}
}
